import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Grid,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from "@mui/material";
import apis from "../utils/apis";
import { Add, Clear, Delete } from "@mui/icons-material";
import Header from "../components/Header";
import { styled, alpha } from "@mui/material/styles";
import { getEmail, saveTargetPath } from "../utils/storage";
import toast, { Toaster } from "react-hot-toast";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom"; // Import useNavigate for navigation
import { beautifyLabel, getOperatorsForField } from "../utils/CommonFunctions";
import CollapsibleTableNew from "../components/common/CollapsibleTableNew";
import TablePagination from "../components/common/TablePagination";

// Styled components for table
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: "#FDF9E0",
  borderColor: "#FDF9E0",
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "rgba(39, 140, 55, 0.1)",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "transparent",
  },
}));

// Utility function to convert months into a human-readable format
const formatExperience = (totalMonths) => {
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  let experienceText = "";
  if (years > 0) {
    experienceText += `${years} ${years > 1 ? "Years" : "Year"}`;
  }
  if (months > 0) {
    experienceText += ` ${months} ${months > 1 ? "Months" : "Month"}`;
  }
  if (totalMonths === 0) {
    return "Fresher/Intern/Trainee";
  }
  return experienceText.trim();
};

function AdvancedDataSearch() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate(); // Initialize useNavigate
  const dispatch = useDispatch();

  // State for API data
  const [fields, setFields] = useState([]);
  const [operators, setOperators] = useState([]);

  // State for filter conditions and filtered data
  const [filterConditions, setFilterConditions] = useState([
    { field: "", operator: "", value: "" },
  ]);
  const [filteredData, setFilteredData] = useState([]);
  const [userData, setUserData] = useState([]); // Placeholder for user data
  // State for pagination
  const [page, setPage] = useState(0); // Default page number
  const [limit, setLimit] = useState(5); // Default limit per page
  const [tableLoading, setTableLoading] = useState(false); // Table-specific loader
  const [totalCount, setTotalCount] = useState(0); // Total count for pagination

  // State for page loading
  const [loading, setLoading] = useState(true); // Loader for the entire page

  // Add validation state
  const [validationErrors, setValidationErrors] = useState([false]);
  const [isSearchDisabled, setIsSearchDisabled] = useState(false);

  const userEmail = getEmail();
  const userRefId = useSelector((state) => state.userReducer.userRefId);
  const superAdmin = useSelector((state) => state.userReducer.superAdmin);

  // Fetch fields and operators from the API
  useEffect(() => {
    const fetchDeepSearchParams = async () => {
      try {
        setLoading(true); // Show loader before API call
        const response = await apis.GET({
          endpoint: "/deep-search-params",
        });

        if (response.status === 201) {
          const deepSearchParams = response.data; // Correctly access the data property

          // Update fields and operators from API response
          setFields(deepSearchParams[0]?.fields || []);
          setOperators(deepSearchParams[1]?.operators || []);
        } else {
          throw new Error("Failed to fetch deep search parameters");
        }
      } catch (error) {
        console.error("Error fetching deep search params:", error);
      } finally {
        setLoading(false); // Hide loader after API call
      }
    };

    fetchDeepSearchParams();
  }, []);

  // Add a new condition and validation error
  const addCondition = () => {
    setFilterConditions([
      ...filterConditions,
      { field: "", operator: "", value: "" },
    ]);
    setValidationErrors([...validationErrors, false]);
  };

  // Update a specific condition and clear its validation error
  const updateCondition = (index, key, value) => {
    const updatedConditions = [...filterConditions];
    updatedConditions[index][key] = value;
    if (key === "field") {
      let defaultValue =
        fields.find((f) => f.name === value)?.defaultValue || "";
      updatedConditions[index]["operator"] = defaultValue;
    }

    setFilterConditions(updatedConditions);

    const updatedValidationErrors = [...validationErrors];
    updatedValidationErrors[index] = false;
    setValidationErrors(updatedValidationErrors);
  };

  // Remove a condition and its validation error
  const removeCondition = (index) => {
    if (filterConditions.length > 1) {
      const updatedConditions = filterConditions.filter((_, i) => i !== index);
      setFilterConditions(updatedConditions);

      const updatedValidationErrors = validationErrors.filter(
        (_, i) => i !== index
      );
      setValidationErrors(updatedValidationErrors);
    }
  };

  // Clear all filters and validation errors
  const clearFilters = () => {
    setFilterConditions([{ field: "", operator: "", value: "" }]);
    setValidationErrors([false]);
    setFilteredData(userData);
  };

  // Validate all filter conditions
  const validateConditions = () => {
    const errors = filterConditions.map(
      (condition) => !condition.field || !condition.operator || !condition.value
    );
    setValidationErrors(errors);
    return !errors.includes(true);
  };

  // Function to send the payload to the API with parameters
  const handleSearch = async (currentPage = page, currentLimit = limit) => {
    // Trim whitespace from all filter condition values before processing
    const trimmedConditions = filterConditions.map((condition) => ({
      ...condition,
      value:
        typeof condition.value === "string"
          ? condition.value.trim()
          : condition.value,
    }));

    if (!validateConditions()) {
      return; // Stop if validation fails
    }

    try {
      setTableLoading(true);
      setIsSearchDisabled(true);

      // Filter out conditions that are incomplete (i.e., any empty field, operator, or value)
      const validConditions = trimmedConditions.filter(
        (condition) => condition.field && condition.operator && condition.value
      );

      if (validConditions.length === 0) {
        setTableLoading(false);
        setIsSearchDisabled(false);
        return;
      }

      // Construct the payload with processed conditions
      const payload = {
        filters: validConditions.map((condition) => {
          if (condition.field === "experience") {
            // Parse the experience value to extract months
            const experienceValue = condition.value.toLowerCase();
            let totalMonths = 0;

            if (/^\d+$/.test(experienceValue)) {
              totalMonths = parseInt(experienceValue, 10) * 12;
            } else {
              const yearsMatch = experienceValue.match(/(\d+)\s*year/);
              const monthsMatch = experienceValue.match(/(\d+)\s*month/);
              if (yearsMatch) totalMonths += parseInt(yearsMatch[1], 10) * 12;
              if (monthsMatch) totalMonths += parseInt(monthsMatch[1], 10);
            }
            return { ...condition, value: totalMonths.toString() };
          }
          return condition;
        }),
        page: currentPage,
        limit: currentLimit,
        superAdmin: superAdmin ?? false, // Assuming superAdmin is false for this search
      };

      // Log the constructed payload for debugging
      console.log("Payload:", payload);

      // Send the payload to the API
      const response = await apis.POST({
        endpoint: "/deep-search-resume",
        payload: payload,
        headers: { "Content-Type": "application/json" },
      });

      if (response.status === 201) {
        console.log("Search Results:", response.data);
        setFilteredData(response.data.data || []);
        setTotalCount(response.data.totalCount || 0);
      } else {
        throw new Error("Failed to fetch search results");
      }
    } catch (error) {
      console.error("Error during search:", error);
    } finally {
      setTableLoading(false);
      setIsSearchDisabled(false);
    }
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    console.log("Changing page to:", newPage);
    const adjustedPage = newPage; // Adjust for zero-based index
    setPage(adjustedPage);
    handleSearch(adjustedPage, limit);
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setLimit(newLimit);
    const newPage = Math.floor((page * limit) / newLimit);
    setPage(newPage);
    handleSearch(1, newLimit);
  };

  const CustomPaper = React.forwardRef(function CustomPaper(props, ref) {
    return (
      <Paper
        ref={ref}
        {...props}
        sx={{
          backgroundColor: "#231F20",
          color: "#FDF9E0",
        }}
      />
    );
  });

  // Function to send email to the user
  const sendEmailToUser = async (row) => {
    try {
      const payload = {
        from: userEmail, // Use the current user's email
        to: row.userform?.email, // Email of the resume owner
        name: row.name, // Name of the resume owner
        referBy: userRefId, // Replace with the recruiter ID
        subject: "A recruiter is evaluating your resume for an exciting job",
      };

      const response = await apis.POST({
        endpoint: "/send-email-to-user-from-recruiter",
        payload: payload,
        options: { type: "json" },
      });

      if (response.status === 201) {
        toast.success("Mail sent successfully!");
      } else {
        toast.error("Failed to send mail. Please try again.");
      }
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error("Failed to send mail. Please try again.");
    }
  };

  const handleViewResume = (row) => {
    // dispatch(setResumeData(row));
    navigate(`/resume-history/${row.uuid}`);
  };

  const handlePublicProfile = (row) => {
    // dispatch(setResumeData(row));
    navigate(`/resume-history/${row.uuid}`);
  };

  // Form submit handler
  const handleFormSubmit = (e) => {
    e.preventDefault();

    handleSearch(1, limit);
  };

  return (
    <Box
      sx={{
        backgroundColor: "#000",
        minHeight: "100vh",
        width: "100%",
        color: "#FDF9E0",
        backgroundImage: "url('../../assets/images/homepage-background.svg')",
        backgroundRepeat: "repeat",
        backgroundSize: "auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        padding: isMobile ? 2 : 4,
      }}
    >
      <Toaster />
      <Header />

      {loading ? (
        // Show loader while the page is loading
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <CircularProgress size={50} />
          <Typography sx={{ mt: 2, color: "#FDF9E0" }}>Loading...</Typography>
        </Box>
      ) : (
        // Show the page content after loading is complete
        <>
          <Typography
            variant={isMobile ? "h5" : "h4"}
            sx={{
              mb: 4,
              textAlign: "center",
              fontWeight: 700,
              fontFamily: "Kanit",
            }}
          >
            Advanced Data Search
          </Typography>

          {/* Begin form for filter conditions */}
          <form
            onSubmit={handleFormSubmit}
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
              alignItems: "center",
            }}
            autoComplete="on"
          >
            {filterConditions.map((condition, index) => (
              <Grid
                container
                spacing={2}
                key={index}
                sx={{
                  mb: 2,
                  maxWidth: { xs: "100%", md: "100%", xl: "90%" },
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "center",
                }}
              >
                <Grid item xs={12} sm={3}>
                  <Autocomplete
                    options={fields}
                    getOptionLabel={(option) => {
                      return beautifyLabel(option?.displayName);
                    }}
                    value={
                      fields.find((f) => f.name === condition.field) || null
                    }
                    onChange={(event, newValue) =>
                      updateCondition(
                        index,
                        "field",
                        newValue ? newValue.name : ""
                      )
                    }
                    PaperComponent={CustomPaper}
                    slotProps={{
                      popper: {
                        modifiers: [
                          {
                            name: "offset",
                            options: { offset: [0, 10] },
                          },
                        ],
                      },
                      paper: {
                        sx: {
                          "& .MuiAutocomplete-noOptions": {
                            backgroundColor: "#231F20",
                            color: "#FDF9E0",
                          },
                        },
                      },
                      listbox: {
                        sx: {
                          backgroundColor: "#231F20",
                          "& .MuiAutocomplete-option": {
                            color: "#FDF9E0",
                            "&:hover": {
                              backgroundColor: alpha("#FDF9E0", 0.1),
                            },
                            "&.Mui-selected": {
                              backgroundColor: alpha("#FDF9E0", 0.2),
                            },
                            "&.Mui-selected:hover": {
                              backgroundColor: alpha("#FDF9E0", 0.25),
                            },
                          },
                          "& .MuiAutocomplete-noOptions": {
                            color: "#FDF9E0",
                          },
                        },
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Field"
                        placeholder="Select Field"
                        error={validationErrors[index] && !condition.field}
                        helperText={
                          validationErrors[index] && !condition.field
                            ? "Field is required"
                            : ""
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "#231F20",
                            "& fieldset": {
                              borderColor: "#FDF9E0",
                            },
                            "&:hover fieldset": {
                              borderColor: "#FDF9E0",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#FDF9E0",
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: "#FDF9E0",
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: "#FDF9E0",
                          },
                          "& .MuiInputBase-input": {
                            color: "#FDF9E0",
                          },
                          "& .MuiFormHelperText-root": {
                            color: "#FDF9E0",
                          },
                          "& svg": {
                            fill: "#FDF9E0 !important",
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Autocomplete
                    options={getOperatorsForField(condition, fields)}
                    getOptionLabel={(option) => option}
                    value={condition.operator || ""}
                    onChange={(event, newValue) =>
                      updateCondition(index, "operator", newValue || "")
                    }
                    PaperComponent={CustomPaper}
                    slotProps={{
                      popper: {
                        modifiers: [
                          {
                            name: "offset",
                            options: { offset: [0, 10] },
                          },
                        ],
                      },
                      paper: {
                        sx: {
                          "& .MuiAutocomplete-noOptions": {
                            backgroundColor: "#231F20",
                            color: "#FDF9E0",
                          },
                        },
                      },
                      listbox: {
                        sx: {
                          backgroundColor: "#231F20",
                          "& .MuiAutocomplete-option": {
                            color: "#FDF9E0",
                            "&:hover": {
                              backgroundColor: alpha("#FDF9E0", 0.1),
                            },
                            "&.Mui-selected": {
                              backgroundColor: alpha("#FDF9E0", 0.2),
                            },
                            "&.Mui-selected:hover": {
                              backgroundColor: alpha("#FDF9E0", 0.25),
                            },
                          },
                          "& .MuiAutocomplete-noOptions": {
                            color: "#FDF9E0",
                          },
                        },
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Operator"
                        placeholder="Select Operator"
                        error={validationErrors[index] && !condition.operator}
                        helperText={
                          validationErrors[index] && !condition.operator
                            ? "Operator is required"
                            : ""
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "#231F20",
                            "& fieldset": {
                              borderColor: "#FDF9E0",
                            },
                            "&:hover fieldset": {
                              borderColor: "#FDF9E0",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#FDF9E0",
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: "#FDF9E0",
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: "#FDF9E0",
                          },
                          "& .MuiInputBase-input": {
                            color: "#FDF9E0",
                          },
                          "& .MuiFormHelperText-root": {
                            color: "#FDF9E0",
                          },
                          "& svg": {
                            fill: "#FDF9E0 !important",
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    value={condition.value}
                    onChange={(e) =>
                      updateCondition(index, "value", e.target.value)
                    }
                    placeholder={
                      fields.find((f) => f.name === condition.field)
                        ?.placeholder || "Enter value"
                    }
                    error={validationErrors[index] && !condition.value}
                    helperText={
                      validationErrors[index] && !condition.value
                        ? "Value is required"
                        : ""
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#231F20",
                        "& fieldset": {
                          borderColor: "#FDF9E0",
                        },
                        "&:hover fieldset": {
                          borderColor: "#FDF9E0",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#FDF9E0",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "#FDF9E0",
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#FDF9E0",
                      },
                      "& .MuiInputBase-input": {
                        color: "#FDF9E0",
                      },
                      "& .MuiFormHelperText-root": {
                        color: "#FDF9E0",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={1} sx={{ textAlign: "center" }}>
                  <IconButton
                    onClick={() => removeCondition(index)}
                    disabled={filterConditions.length === 1}
                    color="error"
                    sx={{
                      "&.Mui-disabled": {
                        color: "#333",
                      },
                    }}
                  >
                    <Delete />
                  </IconButton>
                </Grid>
              </Grid>
            ))}

            <Box sx={{ display: "flex", gap: 2, mt: 3, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Add />}
                onClick={addCondition}
                type="button"
              >
                Add Condition
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Clear />}
                onClick={clearFilters}
                type="button"
              >
                Clear Filters
              </Button>
              <Button
                variant="contained"
                color="success"
                type="submit"
                disabled={
                  isSearchDisabled ||
                  filterConditions.some(
                    (condition) =>
                      !condition.field ||
                      !condition.operator ||
                      !condition.value
                  )
                }
                sx={{
                  "&.Mui-disabled": {
                    backgroundColor: "#333",
                    color: "#000",
                  },
                }}
              >
                Search
              </Button>
            </Box>
          </form>
          {/* End form */}

          {/* Table Section */}
          <Paper
            sx={{
              backgroundColor: "transparent",
              display: "flex",
              flexDirection: "column",
              // justifyContent: "center",
              // alignItems: "center",
              padding: isMobile ? 1 : 3,
              mt: 4,
              width: "100%",
              maxWidth: {
                xs: "100%",
                md: "100%",
                xl: "90%",
              },
            }}
          >
            <TableContainer
              sx={{
                border: "1px solid #FDF9E0",
                borderRadius: "8px",
                overflowX: "auto",
              }}
            >
              <CollapsibleTableNew
                rows={filteredData}
                tableLoading={tableLoading}
                searchQuery={null}
                rowsPerPage={limit}
                page={page}
                collapsible={true}
                tableName={"adjavancedDataSearch"}
              />
            </TableContainer>

            <TablePagination
              page={page}
              rowsPerPage={limit}
              totalCount={totalCount}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              tableLoading={tableLoading}
            />
          </Paper>
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate("/Upload-Resume")} // Redirect to the upload route
              sx={{
                color: "#FDF9E0",
                textTransform: "none",
                borderRadius: "8px",
                padding: "10px 20px",
                "&:hover": {
                  backgroundColor: "#1E6A16",
                },
              }}
            >
              Back to Upload
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}

export default AdvancedDataSearch;
